import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import React, { useState } from 'react';

import { GroupRole } from '@/__generated__/graphql';
import { fetchGroup } from '@/actions/group';
import { notify } from '@/actions/ui';
import Button from '@/components/Button';
import CircularProgress from '@/components/CircularProgress/CircularProgress';
import CountBox from '@/components/CountBox';
import Dialog from '@/components/Dialog';
import Divider from '@/components/Divider';
import GroupMember from '@/components/GroupMember';
import Link from '@/components/Link';
import MemberRequest from '@/components/MemberRequests/Group';
import Pagination from '@/components/Pagination/Pagination';
import Tooltip from '@/componentsv2/Tooltip';
import { money } from '@/core/money';
import { Viewer } from '@/core/viewer';
import { useAppDispatch, useAppSelector } from '@/store';
import { darkGray, primary } from '@/theme/colors';
import { formatCredits } from '@/utils';

import { ActivityGuardModal } from '../team/ActivityGuardModal';
import AddTeamMemberDialog from '../team/AddTeamMemberDialog';
import { TransferOwnershipDialog } from '../team/TransferOwnershipDialog';
import { extractActivityGuardError, useTeamMembers } from '../team/useTeamMembers';

interface TeamMembersProps {
  groupId: string;
}

interface TeamNode {
  id: string;
  role: GroupRole;
  email?: string | null;
  user?: { id: string; name?: string | null } | null;
}

function TeamMemberStats({
  hook,
  isSMB,
  stats,
  creditBalance,
  noSeatsAvailable,
}: {
  hook: ReturnType<typeof useTeamMembers>;
  isSMB: boolean | undefined;
  stats: Record<string, number>;
  creditBalance: ReturnType<typeof money>;
  noSeatsAvailable: boolean;
}) {
  return (
    <div className="mb-16 flex flex-wrap gap-12">
      <CountBox count={hook.totalMembers} label="Team Members" />
      <CountBox count={stats.expert_request_count} label="Expert Requests" />
      <CountBox count={stats.consultation_count} label="Consultations" />
      {isSMB ? (
        <CountBox
          count={`${hook.seatAllocation?.used ?? 0} of ${hook.seatAllocation?.included ?? 0}`}
          label={`${hook.seatAllocation?.included === 1 ? 'Seat' : 'Seats'} Allocated`}
          warn={noSeatsAvailable}
        />
      ) : (
        <CountBox
          count={formatCredits(creditBalance.cents)}
          warn={creditBalance.cents <= 0}
          label="Credits"
        />
      )}
      <div className="ml-auto">
        <TextField
          value={hook.nameSearch ?? ''}
          margin="none"
          fullWidth={false}
          onChange={({ target: { value } }) => {
            hook.refetch({});
            hook.setPage(1);
            hook.setNameSearch(hook.normalizeSpace(value));
          }}
          placeholder="Filter by name"
        />
      </div>
    </div>
  );
}

function TeamMembersList({
  hook,
  groupId,
  isSMB,
  noSeatsAvailable,
  htmlUrl,
  onUpdateMember,
}: {
  hook: ReturnType<typeof useTeamMembers>;
  groupId: string;
  isSMB: boolean | undefined;
  noSeatsAvailable: boolean;
  htmlUrl: string;
  onUpdateMember: (id: string, role: GroupRole) => Promise<void>;
}) {
  const handleAddClose = () => hook.setAddOpen(false);

  return (
    <div className="mt-16 flex">
      <div className="mr-20 min-w-[120px]">
        <div className="text-12 font-bold uppercase text-grey-500">Team Members</div>
        {!hook.perms.add ? (
          <Tooltip title="You do not have permission to add members.">
            <span>
              <Button color="white" variant="text" fontColor={primary} size="small" disabled>
                Add
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            onClick={() => hook.setAddOpen(true)}
            color="white"
            variant="text"
            fontColor={primary}
            size="small"
          >
            Add
          </Button>
        )}
        <AddTeamMemberDialog
          groupId={groupId}
          userFilter={hook.addUserFilter}
          open={hook.addOpen}
          onSubmit={() => {
            hook.setAddTeamMemberLoading(true);
            handleAddClose();
          }}
          onClose={handleAddClose}
          onRequestDone={() => {
            hook.setAddTeamMemberLoading(false);
            hook.fetchPageMembers();
            hook.refreshSeatAllocation();
          }}
          addTeamMember={hook.addTeamMember}
          inviteUser={hook.inviteUser}
          isSMB={isSMB}
          seatsAvailable={isSMB ? (hook.seatAllocation?.available ?? 0) : undefined}
          settingsUrl={isSMB ? htmlUrl : undefined}
        />
      </div>

      <div className="flex-1">
        {hook.isLoading ? (
          <div className="flex flex-col items-center py-20">
            <CircularProgress size={52} />
            <span>Loading...</span>
          </div>
        ) : (
          hook.members.edges.map(({ node: m }: { node: TeamNode }) => (
            <GroupMember
              key={m.id}
              member={m}
              groupId={groupId}
              removeGroupMember={hook.canRemoveMember(m) ? hook.removeTeamMemberAndFetch : null}
              updateGroupMember={
                hook.perms.update[m.role as keyof typeof hook.perms.update]
                  ? isSMB
                    ? onUpdateMember
                    : hook.updateTeamMemberAndFetch
                  : null
              }
              allowedRoles={hook.perms.allowedRoles
                .filter((r: string) => !isSMB || r !== 'admin')
                // Viewer is an SMB-only role on credit/enterprise accounts.
                // Keep it in options only when the row already has it (so
                // pre-existing viewer members still render their current role)
                // but never let a new role be set to viewer.
                .filter((r: string) => isSMB || r !== 'viewer' || m.role === 'viewer')
                .filter((r: string) => m.user || r !== 'owner')}
              disabledRoles={isSMB && noSeatsAvailable && m.role === 'viewer' ? ['member'] : []}
            />
          ))
        )}
        {hook.totalPages > 1 && (
          <div className="flex justify-center py-16">
            <Pagination
              page={hook.page}
              count={hook.totalPages}
              onChange={(_: React.ChangeEvent<unknown>, value: number) => {
                hook.refetch({ page: value });
                hook.setPage(value);
              }}
            />
          </div>
        )}
        {isSMB && noSeatsAvailable && (
          <div style={{ marginTop: 42, fontSize: 14, color: darkGray }}>
            Add seats in{' '}
            <Link to={`${htmlUrl}/settings`} className="hover:underline">
              Settings &amp; Billing
            </Link>
            .
          </div>
        )}
      </div>
    </div>
  );
}

function TeamMembersContent({
  viewer,
  team,
  groupId,
}: {
  viewer: Viewer;
  team: Record<string, unknown>;
  groupId: string;
}) {
  const dispatch = useAppDispatch();
  const [selfDemoteConfirm, setSelfDemoteConfirm] = useState(false);

  const hook = useTeamMembers({
    viewer,
    groupId,
    team,
    notify: (...args: [string, string?]) => dispatch(notify(...args)),
    fetchGroup: (...args: [string, Record<string, boolean>?]) =>
      dispatch(fetchGroup(...(args as Parameters<typeof fetchGroup>))),
  });

  const { stats = {}, html_url: htmlUrl = '' } = team as {
    stats?: Record<string, number>;
    html_url?: string;
  };
  const creditBalance = hook.billingAccount
    ? money(hook.billingAccount.credit_balance)
    : money({ cents: 0, currency: 'OFC' });
  const isSMB = (team as { billing_account?: { isSMBAccount?: boolean } }).billing_account
    ?.isSMBAccount;

  const isOverCapacity =
    hook.seatAllocation && hook.seatAllocation.used > hook.seatAllocation.included;
  const noSeatsAvailable = !!(hook.seatAllocation && hook.seatAllocation.available <= 0);

  const handleUpdateWithSelfDemotion = async (id: string, role: GroupRole) => {
    const memberNode = hook.members.edges.find(
      ({ node: m }: { node: TeamNode }) => m.id === id
    )?.node;
    if (role === GroupRole.Viewer && memberNode?.user?.id === viewer.id) {
      setSelfDemoteConfirm(true);
      return;
    }
    await hook.updateTeamMemberAndFetch(id, role);
  };

  const handleSelfDemoteConfirm = async () => {
    const viewerNode = hook.members.edges.find(
      ({ node: m }: { node: TeamNode }) => m.user?.id === viewer.id
    )?.node;
    if (!viewerNode) return;
    setSelfDemoteConfirm(false);
    try {
      await hook.updateTeamMember({ variables: { id: viewerNode.id, role: GroupRole.Viewer } });
      hook.refreshSeatAllocation();
    } catch (err: unknown) {
      const guard = extractActivityGuardError(err);
      if (guard) {
        hook.setActivityGuardError(guard);
      } else {
        const gqlErr = err as { graphQLErrors?: Array<{ message: string }> };
        dispatch(notify(gqlErr?.graphQLErrors?.[0]?.message || 'Failed to update role', 'error'));
      }
    }
  };

  return (
    <div>
      <TeamMemberStats
        hook={hook}
        isSMB={isSMB}
        stats={stats}
        creditBalance={creditBalance}
        noSeatsAvailable={noSeatsAvailable}
      />
      <Divider />

      {isSMB && isOverCapacity && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your team has more members than available seats. Add seats or change some members to
          Viewers.{' '}
          <Link to={`${htmlUrl}/settings/subscription`} style={{ fontWeight: 600 }}>
            Manage Subscription
          </Link>
        </Alert>
      )}

      {hook.awaitingMembersData && (
        <MemberRequest
          onApprove={hook.fetchPageMembers}
          memberRequests={hook.awaitingMembersData}
        />
      )}

      <TeamMembersList
        hook={hook}
        groupId={groupId}
        isSMB={isSMB}
        noSeatsAvailable={noSeatsAvailable}
        htmlUrl={htmlUrl}
        onUpdateMember={handleUpdateWithSelfDemotion}
      />

      <ActivityGuardModal
        open={!!hook.activityGuardError}
        onClose={() => hook.setActivityGuardError(null)}
        error={hook.activityGuardError}
      />

      {isSMB && (
        <TransferOwnershipDialog
          open={!!hook.transferTarget}
          onClose={() => hook.setTransferTarget(null)}
          onConfirm={hook.handleTransferConfirm}
          submitting={hook.transferLoading}
          targetMember={hook.transferTarget}
          currentOwner={hook.currentOwner}
        />
      )}

      {isSMB && (
        <Dialog
          open={selfDemoteConfirm}
          title="Demote yourself to Viewer?"
          onConfirm={handleSelfDemoteConfirm}
          onCancel={() => setSelfDemoteConfirm(false)}
          confirmLabel="Demote to Viewer"
          confirmButtonProps={{ color: 'red' }}
        >
          <p>
            You&apos;ll lose the ability to create expert requests and book consultations. You can
            ask the team owner to restore your Member seat later.
          </p>
        </Dialog>
      )}
    </div>
  );
}

export default function TeamMembers({ groupId }: TeamMembersProps): React.ReactElement {
  const viewer = useAppSelector((state) => state.viewer);
  const team = useAppSelector((state) =>
    state.groups.all.edges.find((g: { node: { id: string } }) => g.node.id === groupId)
  )?.node;

  if (!team) {
    return (
      <div className="flex flex-col items-center py-20">
        <CircularProgress size={52} />
        <span>Loading...</span>
      </div>
    );
  }

  return <TeamMembersContent viewer={viewer} team={team} groupId={groupId} />;
}
